/**
 * KontrollbereichPanel — the Kontrollbereich Cockpit panel on the Portfolio Dashboard
 * (Berechtigungskonzept V4.0 · Bereich 5, Kapitel 8). NOT a nav item — it sits below the
 * KPI tiles on /portfolio.
 *
 * Header (clock icon + "Kontrollbereich" + subtitle + "{offen_total} offen total"), 4 stage
 * tiles (STUFE 1-4: count · name · "≥ N Tage" · status dot; cream/orange/salmon/red), a
 * 4-segment gradient bar weighted by count, and a footer note. Clicking a tile routes to the
 * filtered Kontrollliste. The whole panel is gated on administration.kontrolle.rueckstand.einsehen
 * (the effective-permission hook) — the api is the hard line; this is guard-awareness only.
 *
 * [Source: V4.0-inbox/cockpit-screenshots/image1,6,9.png] (layout + colours + labels)
 * [Source: projects/broker-ag/berechtigungen/bereich5-cockpit-build-spec.md §"UI spec"]
 */

import { Clock } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { Card } from '../../components/ui/Card';
import { cn } from '../../lib/cn';
import { useEffectivePermissions } from '../administration/useEffectivePermissions';
import { useKontrolleUebersicht } from './hooks';
import { STUFE_CLASSES, KONTROLLE_EINSEHEN_KEY } from './constants';
import type { KontrolleUebersichtStufeDto } from './types';

function StageTile({
  stufe,
  onOpen,
}: {
  stufe: KontrolleUebersichtStufeDto;
  onOpen: () => void;
}) {
  const c = STUFE_CLASSES[stufe.stufe];
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        'flex flex-col rounded-xl border p-4 text-left transition-shadow hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        c.tileBg,
        c.border,
      )}
    >
      <div className="flex items-start justify-between">
        <span className={cn('text-[11px] font-semibold uppercase tracking-wide', c.text)}>
          Stufe {stufe.stufe}
        </span>
        <span className={cn('mt-0.5 size-2 shrink-0 rounded-full', c.dot)} aria-hidden="true" />
      </div>
      <span className="mt-1 text-2xl font-semibold tabular-nums text-foreground">{stufe.count}</span>
      <span className={cn('mt-1 text-sm font-semibold', c.text)}>{stufe.name}</span>
      <span className="mt-0.5 text-xs text-muted-foreground">≥ {stufe.schwelle_tage} Tage</span>
    </button>
  );
}

/** The 4-segment gradient bar (cream → red), each segment weighted by its stage count. */
function GradientBar({ stufen }: { stufen: KontrolleUebersichtStufeDto[] }) {
  return (
    <div className="mt-4 flex h-1.5 gap-0.5 overflow-hidden rounded-full">
      {stufen.map((s) => (
        <div
          key={s.stufe}
          className={cn('h-full rounded-full', STUFE_CLASSES[s.stufe].bar)}
          // +0.5 basis so an empty stage still shows a sliver (never vanishes).
          style={{ flexGrow: s.count + 0.5 }}
        />
      ))}
    </div>
  );
}

export function KontrollbereichPanel() {
  const access = useEffectivePermissions();
  const navigate = useNavigate();
  const { data, isError } = useKontrolleUebersicht(access.can(KONTROLLE_EINSEHEN_KEY));

  // Guard-awareness: hide the whole panel without the einsehen permission. This is the only
  // path that keeps the panel invisible; a failed load renders the error Card below instead.
  if (!access.can(KONTROLLE_EINSEHEN_KEY)) return null;

  // A failed load must not look like "nothing to show": stay visible and say so.
  if (isError) {
    return (
      <Card className="rounded-xl p-5" data-testid="kontrollbereich-panel">
        <p className="text-sm text-muted-foreground" role="alert">
          Kontrollbereich konnte nicht geladen werden.
        </p>
      </Card>
    );
  }

  // Still in flight (mock mode carries initialData, so this is a live-mode flash only).
  if (!data) return null;

  return (
    <Card
      className={cn(
        'rounded-xl p-5',
        data.highlight && 'ring-1 ring-stage-4-border',
      )}
      data-testid="kontrollbereich-panel"
    >
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-2.5">
          <Clock className="mt-0.5 size-5 text-stage-3-text" aria-hidden="true" />
          <div>
            <h2 className="text-base font-semibold text-foreground">Kontrollbereich</h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Unbearbeitete Posteingänge über alle Kanäle · Bereich 5, Kapitel 8
            </p>
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-semibold tabular-nums text-foreground">
            {data.offen_total}
          </div>
          <div className="text-xs text-muted-foreground">offen total</div>
        </div>
      </div>

      {/* 4 stage tiles */}
      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {data.stufen.map((s) => (
          <StageTile
            key={s.stufe}
            stufe={s}
            onOpen={() =>
              void navigate({ to: '/portfolio/kontrolle/$stufe', params: { stufe: String(s.stufe) } })
            }
          />
        ))}
      </div>

      {/* Gradient bar */}
      <GradientBar stufen={data.stufen} />

      {/* Footer note */}
      <p className="mt-3 text-xs text-muted-foreground">
        Klick auf eine Stufe öffnet die gefilterte Liste · Fristen pro Broker konfigurierbar
        (Kapitel 7)
      </p>
    </Card>
  );
}
